import React, { useState } from "react";
import { useContext, useEffect, useMemo } from "react";
import { AuthContext } from "@src/auth/components/AuthProvider";
import {
  EndCustomer,
  ServiceType,
  useGetServiceTypesLazyQuery,
} from "@api/graphql/generated/generated-types";
import { Select } from "@src/common/components";
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  RegisterOptions,
} from "react-hook-form";
import "react-datepicker/dist/react-datepicker.css";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import { useCourieStore } from "@src/common/lib/store";

type Props = {
  control: Control<FieldValues, any>;
  errors: FieldErrors<FieldValues>;
  onChange?: (value: ServiceType | undefined) => void;
  size?: "mini" | "large" | "default" | "compact" | undefined;
  value?: any;
  rules: RegisterOptions;
  endCustomer?: EndCustomer | undefined;
  onServiceTypesLoaded?: (
    serviceTypes: ServiceType[],
    forEndCustomerId: string | null
  ) => void;
};

function ServiceTypeSelector({
  control,
  onChange: onChangeProp,
  size,
  value,
  rules,
  endCustomer,
  onServiceTypesLoaded,
}: Props) {
  const { courierId } = useContext(AuthContext);
  const { showToast } = useCourieStore();
  const [
    getServiceTypes,
    {
      data: getServiceTypesQueryData,
      loading: getServiceTypesQueryLoading,
      variables: getServiceTypesQueryVariables,
    },
  ] = useGetServiceTypesLazyQuery({fetchPolicy: "network-only"});
  const [initialValue, setInitialValue] = useState<ServiceType | null>(null);

  useEffect(() => {
    if (courierId) {
      getServiceTypes({
        variables: {
          courierId: courierId!,
          endCustomerId: endCustomer?.id || null,
          // If there is endCUstomer selected, show the only available service types for that endCustomer
          //          so that dispatchers can make no mistake selecting the wrong service type
          // Else, show all service types (showForEndCustomer = false)
          showForEndCustomer: endCustomer !== undefined && endCustomer !== null,
          includeInternalOnly: true
        },
      }).catch((e) => {
        showErrorToast(e, showToast);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courierId, endCustomer]);

  useEffect(() => {
    if (value && getServiceTypesQueryData) {
      const foundServiceType = getServiceTypesQueryData.serviceTypes.find(
        (serviceType) => serviceType.id === value
      );
      setInitialValue((foundServiceType as ServiceType) || null);
    }
    if (value === undefined) {
      setInitialValue(null);
    }
  }, [getServiceTypesQueryData, value]);

  const formattedOptions: ServiceType[] = useMemo(() => {
    if (!getServiceTypesQueryData) {
      return [];
    }
    return getServiceTypesQueryData.serviceTypes
      .map((serviceType) => ({
        ...serviceType,
        name: `${serviceType.name} ${
          serviceType.deliveryTimeInMinutes
            ? `(${formatDeliveryTime(serviceType.deliveryTimeInMinutes)})`
            : ""
        }`,
      }))
      .sort((a, b) => {
        const aRank = a.orderingSequenceNumber ?? Infinity;
        const bRank = b.orderingSequenceNumber ?? Infinity;
        if (aRank !== bRank) return aRank - bRank;
        return a.name.localeCompare(b.name);
      }) as ServiceType[];
    // formatDeliveryTime is a pure function declared in this component body
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getServiceTypesQueryData]);

  useEffect(() => {
    if (getServiceTypesQueryData) {
      onServiceTypesLoaded?.(
        formattedOptions,
        getServiceTypesQueryVariables?.endCustomerId ?? null
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedOptions]);

  function formatDeliveryTime(minutes: number) {
    if (minutes < 120) return `${minutes} min`;

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    return `${hours} hr${hours > 1 ? "s" : ""}${
      remainingMinutes > 0 ? ` ${remainingMinutes} min` : ""
    }`;
  }

  return (
    <div className="flex">
      <Controller
        control={control}
        name="serviceType"
        rules={rules}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          if (initialValue && !value) {
            onChange(initialValue);
          }
          return (
            <Select
              size={size || undefined}
              error={error ? true : false}
              options={formattedOptions}
              value={initialValue ? [initialValue] : value ? [value] : []} // Pass the entire object or objects
              labelKey="name"
              valueKey="id"
              isLoading={getServiceTypesQueryLoading}
              placeholder="Service Type"
              onChange={(params: any) => {
                const value: ServiceType | undefined = params.value[0];
                onChange(value);
                onChangeProp && onChangeProp(value);
              }}
            />
          );
        }}
      />
    </div>
  );
}

export default ServiceTypeSelector;
